import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LLMCFG, LLMKEYS } from "@/constants/testIds";
import { Save, Sliders, Zap, DollarSign, KeyRound, Eye, EyeOff, Trash2 } from "lucide-react";

function fmt$(v) {
    if (v == null) return "—";
    if (v === 0) return "$0";
    return `$${v.toFixed(2)}`;
}

export default function LLMConfigPage() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [err, setErr] = useState(null);

    // Local editable state
    const [enabled, setEnabled] = useState({});
    const [agentModels, setAgentModels] = useState({});
    const [agentMaxTokens, setAgentMaxTokens] = useState({});
    const [cacheEnabled, setCacheEnabled] = useState(true);
    const [cacheTTL, setCacheTTL] = useState(30);
    const [softPct, setSoftPct] = useState(80);
    const [keySource, setKeySource] = useState("emergent");
    const [providerKeys, setProviderKeys] = useState({ openai: "", anthropic: "", gemini: "" });
    const [maskedKeys, setMaskedKeys] = useState({ openai: "", anthropic: "", gemini: "" });
    const [revealed, setRevealed] = useState({ openai: false, anthropic: false, gemini: false });
    const [dirtyKey, setDirtyKey] = useState({ openai: false, anthropic: false, gemini: false });

    const load = async () => {
        try {
            const res = await api.get("/llm-config");
            setData(res.data);
            const cfg = res.data.config;
            const en = {};
            (res.data.available_llms || []).forEach((l) => {
                en[l.key] = (cfg.enabled_llm_keys || []).includes(l.key);
            });
            setEnabled(en);
            setAgentModels({ ...(cfg.agent_models || {}) });
            setAgentMaxTokens({ ...(cfg.agent_max_tokens || {}) });
            setCacheEnabled(Boolean(cfg.context_cache_enabled));
            setCacheTTL(Number(cfg.context_cache_ttl_minutes || 30));
            setSoftPct(Number(cfg.org_soft_threshold_pct || 80));
            setKeySource(cfg.key_source || "emergent");
            const masked = cfg.provider_keys || {};
            setMaskedKeys({
                openai: masked.openai || "",
                anthropic: masked.anthropic || "",
                gemini: masked.gemini || "",
            });
            // Reset editable inputs to the masked value (so we don't force the admin to re-enter)
            setProviderKeys({
                openai: masked.openai || "",
                anthropic: masked.anthropic || "",
                gemini: masked.gemini || "",
            });
            setDirtyKey({ openai: false, anthropic: false, gemini: false });
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        setSaving(true);
        setErr(null);
        try {
            const enabled_llm_keys = Object.entries(enabled).filter(([, v]) => v).map(([k]) => k);
            const cleanedModels = {};
            Object.entries(agentModels).forEach(([k, v]) => {
                if (v && v !== "__default__") cleanedModels[k] = v;
            });
            const cleanedMax = {};
            Object.entries(agentMaxTokens).forEach(([k, v]) => {
                const n = Number(v);
                if (Number.isFinite(n) && n > 0) cleanedMax[k] = Math.floor(n);
            });
            await api.put("/llm-config", {
                enabled_llm_keys,
                agent_models: cleanedModels,
                agent_max_tokens: cleanedMax,
                context_cache_enabled: cacheEnabled,
                context_cache_ttl_minutes: Number(cacheTTL) || 30,
                org_soft_threshold_pct: Number(softPct) || 80,
                key_source: keySource,
                // Only send provider keys the admin actually changed;
                // otherwise the backend would try to overwrite with the masked placeholder.
                provider_keys: Object.fromEntries(
                    ["openai", "anthropic", "gemini"]
                        .filter((p) => dirtyKey[p])
                        .map((p) => [p, providerKeys[p] || ""]),
                ),
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 1500);
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (!data) {
        return (
            <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 text-center text-sm text-[color:var(--text-muted)] font-light">
                Loading LLM configuration…
            </div>
        );
    }

    return (
        <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8 lg:py-10 space-y-8">
            <div className="flex items-baseline justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                        Admin · LLM Configuration
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--text-main)] leading-tight mt-1">
                        Prompt Optimization Engine
                    </h2>
                    <p className="text-sm text-[color:var(--text-muted)] font-light mt-2 max-w-2xl">
                        Enable providers, pin cheaper models to specific agents to trim cost,
                        cap max tokens per agent, and control context caching to reuse recent
                        pipeline runs without spending tokens twice.
                    </p>
                </div>
                <button
                    data-testid={LLMCFG.saveButton}
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[color:var(--profecia-gold)] text-white text-sm font-medium hover:bg-[color:var(--profecia-gold-hover)] disabled:opacity-50 transition-colors"
                >
                    <Save size={14} /> {saved ? "Saved ✓" : saving ? "Saving…" : "Save Configuration"}
                </button>
            </div>

            {err && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-2">
                    {String(err)}
                </div>
            )}

            {/* Enable providers */}
            <section className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-6">
                <div className="flex items-baseline justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                            Providers
                        </div>
                        <h3 className="font-serif text-xl text-[color:var(--text-main)] leading-none mt-1">
                            Enabled LLMs
                        </h3>
                    </div>
                    <Zap size={16} className="text-[color:var(--profecia-gold)]" />
                </div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {data.available_llms.map((l) => (
                        <li
                            key={l.key}
                            className="flex items-center gap-3 px-4 py-3 bg-[color:var(--surface-hover)] rounded-md border border-[color:var(--border-subtle)]"
                        >
                            <label className="flex items-center gap-3 cursor-pointer flex-1">
                                <input
                                    data-testid={LLMCFG.enableToggle(l.key)}
                                    type="checkbox"
                                    checked={!!enabled[l.key]}
                                    onChange={(e) =>
                                        setEnabled((prev) => ({ ...prev, [l.key]: e.target.checked }))
                                    }
                                    className="w-4 h-4 accent-[color:var(--profecia-gold)]"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[color:var(--text-main)]">{l.label}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mt-0.5">
                                        {l.provider} · {l.model}
                                    </div>
                                </div>
                            </label>
                            <div className="text-right text-[11px] font-mono text-[color:var(--text-muted)]">
                                <div>in {fmt$(l.price_in_per_m)}/M</div>
                                <div>out {fmt$(l.price_out_per_m)}/M</div>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            {/* API Keys */}
            <section className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-6">
                <div className="flex items-baseline justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                            API Keys
                        </div>
                        <h3 className="font-serif text-xl text-[color:var(--text-main)] leading-none mt-1">
                            Which key powers the pipeline?
                        </h3>
                        <p className="text-xs text-[color:var(--text-muted)] font-light mt-1 max-w-2xl">
                            The Emergent Universal Key covers OpenAI, Anthropic, and Gemini
                            with a single top-up. Switch to Custom to route requests through
                            your own per-provider keys instead — usage will bill to those
                            provider accounts directly.
                        </p>
                    </div>
                    <KeyRound size={16} className="text-[color:var(--profecia-gold)]" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                    <button
                        type="button"
                        data-testid={LLMKEYS.keySourceEmergent}
                        onClick={() => setKeySource("emergent")}
                        className={`text-left px-4 py-3 rounded-md border transition-colors ${
                            keySource === "emergent"
                                ? "border-[color:var(--profecia-gold)] bg-[color:var(--profecia-gold-light)]"
                                : "border-[color:var(--border-subtle)] hover:border-[color:var(--profecia-gold-subtle)] bg-[color:var(--surface-hover)]"
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${keySource === "emergent" ? "bg-[color:var(--profecia-gold)]" : "bg-[color:var(--border-subtle)]"}`} />
                            <span className="text-sm font-medium text-[color:var(--text-main)]">
                                Emergent Universal Key
                            </span>
                            <span className="ml-auto text-[10px] uppercase tracking-widest font-mono text-[color:var(--profecia-gold)]">
                                default
                            </span>
                        </div>
                        <p className="text-[11px] text-[color:var(--text-muted)] font-light mt-1">
                            Zero setup — one key covers all three providers, credits deducted from your Universal Key balance.
                        </p>
                    </button>
                    <button
                        type="button"
                        data-testid={LLMKEYS.keySourceCustom}
                        onClick={() => setKeySource("custom")}
                        className={`text-left px-4 py-3 rounded-md border transition-colors ${
                            keySource === "custom"
                                ? "border-[color:var(--profecia-gold)] bg-[color:var(--profecia-gold-light)]"
                                : "border-[color:var(--border-subtle)] hover:border-[color:var(--profecia-gold-subtle)] bg-[color:var(--surface-hover)]"
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${keySource === "custom" ? "bg-[color:var(--profecia-gold)]" : "bg-[color:var(--border-subtle)]"}`} />
                            <span className="text-sm font-medium text-[color:var(--text-main)]">
                                Custom Provider Keys
                            </span>
                        </div>
                        <p className="text-[11px] text-[color:var(--text-muted)] font-light mt-1">
                            Bring your own OpenAI / Anthropic / Gemini keys — usage bills directly to your provider accounts.
                        </p>
                    </button>
                </div>

                {keySource === "custom" && (
                    <div className="space-y-3">
                        {[
                            { p: "anthropic", label: "Anthropic API Key", hint: "For Claude Sonnet 4.5 / Haiku 4.5", tid: LLMKEYS.inputAnthropic },
                            { p: "openai", label: "OpenAI API Key", hint: "For GPT-5.x models", tid: LLMKEYS.inputOpenAI },
                            { p: "gemini", label: "Google Gemini API Key", hint: "For Gemini 3 Flash", tid: LLMKEYS.inputGemini },
                        ].map(({ p, label, hint, tid }) => (
                            <div key={p} className="grid grid-cols-1 md:grid-cols-[220px,1fr,auto] gap-3 items-center">
                                <div>
                                    <div className="text-sm text-[color:var(--text-main)]">{label}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mt-0.5">
                                        {hint}
                                    </div>
                                </div>
                                <div className="relative">
                                    <input
                                        data-testid={tid}
                                        type={revealed[p] ? "text" : "password"}
                                        value={providerKeys[p]}
                                        placeholder={maskedKeys[p] ? "Enter new key to replace" : "sk-…"}
                                        onFocus={() => {
                                            // When user focuses a field that still shows the masked value, clear it
                                            if (providerKeys[p] === maskedKeys[p] && maskedKeys[p].includes("•")) {
                                                setProviderKeys((prev) => ({ ...prev, [p]: "" }));
                                                setDirtyKey((prev) => ({ ...prev, [p]: true }));
                                            }
                                        }}
                                        onChange={(e) => {
                                            setProviderKeys((prev) => ({ ...prev, [p]: e.target.value }));
                                            setDirtyKey((prev) => ({ ...prev, [p]: true }));
                                        }}
                                        className="w-full px-3 py-2.5 pr-10 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] font-mono text-sm"
                                    />
                                    <button
                                        type="button"
                                        data-testid={LLMKEYS.revealToggle(p)}
                                        onClick={() => setRevealed((prev) => ({ ...prev, [p]: !prev[p] }))}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-main)]"
                                        aria-label={revealed[p] ? "Hide" : "Reveal"}
                                    >
                                        {revealed[p] ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    data-testid={LLMKEYS.clearButton(p)}
                                    onClick={() => {
                                        setProviderKeys((prev) => ({ ...prev, [p]: "" }));
                                        setDirtyKey((prev) => ({ ...prev, [p]: true }));
                                    }}
                                    className="p-2 text-[color:var(--text-muted)] hover:text-red-600 transition-colors"
                                    title="Clear this key"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                        <div className="text-[11px] text-[color:var(--text-muted)] font-light">
                            Keys are stored encrypted at rest in your workspace. Only the last 4 characters
                            are ever returned to the browser after saving.
                        </div>
                    </div>
                )}
            </section>

            {/* Per-agent model & token cap */}
            <section className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-6">
                <div className="flex items-baseline justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                            Per-agent overrides
                        </div>
                        <h3 className="font-serif text-xl text-[color:var(--text-main)] leading-none mt-1">
                            Model + token cap per agent
                        </h3>
                        <p className="text-xs text-[color:var(--text-muted)] font-light mt-1 max-w-xl">
                            &ldquo;Default&rdquo; means each agent uses whatever the operator picks at
                            run time. Overriding here forces a specific model for that agent
                            regardless — useful for pinning cheap models to early agents.
                        </p>
                    </div>
                    <Sliders size={16} className="text-[color:var(--profecia-gold)]" />
                </div>

                <table className="w-full text-sm">
                    <thead className="text-left text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)]">
                        <tr>
                            <th className="py-2">Agent</th>
                            <th className="py-2">Model override</th>
                            <th className="py-2">Max tokens</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.agents.map((a) => (
                            <tr key={a.id} className="border-t border-[color:var(--border-subtle)]">
                                <td className="py-3 pr-6">
                                    <div className="text-[color:var(--text-main)] font-medium">{a.name}</div>
                                    <div className="text-[11px] text-[color:var(--text-muted)] font-light">{a.desc}</div>
                                </td>
                                <td className="py-3 pr-6">
                                    <select
                                        data-testid={LLMCFG.agentSelect(a.id)}
                                        value={agentModels[a.id] || "__default__"}
                                        onChange={(e) =>
                                            setAgentModels((prev) => ({
                                                ...prev,
                                                [a.id]: e.target.value === "__default__" ? "" : e.target.value,
                                            }))
                                        }
                                        className="native-select"
                                    >
                                        <option value="__default__">— Use run-time selection —</option>
                                        {data.available_llms.map((l) => (
                                            <option key={l.key} value={l.key}>
                                                {l.label}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="py-3">
                                    <input
                                        type="number"
                                        min="0"
                                        step="256"
                                        placeholder="default"
                                        value={agentMaxTokens[a.id] || ""}
                                        onChange={(e) =>
                                            setAgentMaxTokens((prev) => ({
                                                ...prev,
                                                [a.id]: e.target.value,
                                            }))
                                        }
                                        className="w-32 px-2 py-2 rounded border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] font-mono text-sm"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <style>{`
                    .native-select {
                        appearance: none;
                        background-color: var(--surface-hover);
                        background-image: url("data:image/svg+xml;utf8,<svg fill='%23767269' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 011.08 1.04l-4.25 4.4a.75.75 0 01-1.08 0l-4.25-4.4a.75.75 0 01.02-1.06z' clip-rule='evenodd'/></svg>");
                        background-repeat: no-repeat;
                        background-position: right 10px center;
                        background-size: 14px;
                        border: 1px solid var(--border-subtle);
                        border-radius: 6px;
                        padding: 8px 32px 8px 12px;
                        font-family: "Manrope", sans-serif;
                        font-size: 13px;
                        color: var(--text-main);
                        min-width: 240px;
                        cursor: pointer;
                    }
                    .native-select:focus {
                        outline: none;
                        border-color: var(--profecia-gold);
                        background-color: #fff;
                    }
                `}</style>
            </section>

            {/* Context caching */}
            <section className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-6">
                <div className="flex items-baseline justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                            Context caching
                        </div>
                        <h3 className="font-serif text-xl text-[color:var(--text-main)] leading-none mt-1">
                            Reuse recent pipeline runs
                        </h3>
                        <p className="text-xs text-[color:var(--text-muted)] font-light mt-1 max-w-xl">
                            When ON, an identical brief + brand + reference set within the TTL
                            window returns the same output at zero token cost. Changes to
                            prompt library or per-agent models automatically bust the cache.
                        </p>
                    </div>
                    <DollarSign size={16} className="text-[color:var(--profecia-gold)]" />
                </div>
                <div className="flex flex-wrap gap-6 items-end">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            data-testid={LLMCFG.cacheToggle}
                            type="checkbox"
                            checked={cacheEnabled}
                            onChange={(e) => setCacheEnabled(e.target.checked)}
                            className="w-4 h-4 accent-[color:var(--profecia-gold)]"
                        />
                        <span className="text-sm text-[color:var(--text-main)]">Enable context cache</span>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-1.5 block">
                            Cache TTL (minutes)
                        </span>
                        <input
                            data-testid={LLMCFG.cacheTtlInput}
                            type="number"
                            min="1"
                            max="1440"
                            value={cacheTTL}
                            onChange={(e) => setCacheTTL(e.target.value)}
                            className="w-28 px-2 py-2 rounded border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] font-mono text-sm"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-1.5 block">
                            Budget soft-alert threshold (%)
                        </span>
                        <input
                            type="number"
                            min="10"
                            max="99"
                            value={softPct}
                            onChange={(e) => setSoftPct(e.target.value)}
                            className="w-28 px-2 py-2 rounded border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] font-mono text-sm"
                        />
                    </label>
                </div>
            </section>
        </main>
    );
}
